import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, collection, writeBatch, serverTimestamp, getDoc } from 'firebase/firestore';
import { FcGoogle } from 'react-icons/fc';
import { PiShieldCheck } from 'react-icons/pi';
import { auth, db } from '../firebaseConfig';
import '../styles/Auth.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const getDestination = async (user) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const orgId = userDoc.data()?.orgId;
      if (!orgId) return '/dashboard';
      const memberDoc = await getDoc(doc(db, 'orgs', orgId, 'members', user.uid));
      return memberDoc.data()?.role === 'admin' ? '/dashboard' : '/rep/dashboard';
    } catch (_) {
      return '/dashboard';
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user && !loading) {
        const dest = await getDestination(user);
        navigate(dest, { replace: true });
      }
    });
    return () => unsub();
  }, [navigate, loading]);

  const provisionNewOrg = async (user) => {
    const orgRef = doc(collection(db, 'orgs'));
    const orgId = orgRef.id;
    const domain = user.email?.split('@')[1]?.toLowerCase();
    const orgName = domain ? `${domain} Organization` : 'My Organization';

    const batch = writeBatch(db);

    batch.set(orgRef, {
      name: orgName,
      domain: domain || null,
      createdBy: user.uid,
      createdAt: serverTimestamp(),
    });

    const memberRef = doc(db, 'orgs', orgId, 'members', user.uid);
    batch.set(memberRef, {
      uid: user.uid,
      email: user.email || null,
      role: 'admin',
      status: 'active',
      joinedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const userRef = doc(db, 'users', user.uid);
    batch.set(userRef, {
      uid: user.uid,
      email: user.email,
      orgId,
      createdAt: serverTimestamp(),
    }, { merge: true });

    await batch.commit();
  };

  const ensureProvisionedUser = async (user) => {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : null;
    if (!userData?.orgId) {
      await provisionNewOrg(user);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await ensureProvisionedUser(cred.user);
      navigate(await getDestination(cred.user));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    const provider = new GoogleAuthProvider();

    try {
      const cred = await signInWithPopup(auth, provider);
      await ensureProvisionedUser(cred.user);
      navigate(await getDestination(cred.user));
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Link to="/" className="auth-brand">UpConvert</Link>

      <div className="auth-card">
        <div className="auth-header">
          <p className="eyebrow">Welcome back</p>
          <h1>Log in to your account</h1>
          <p className="subhead">Access your workspace and continue closing deals.</p>
        </div>

        <form onSubmit={handleLogin} className="auth-form">
          <label className="input-label">
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </label>

          <label className="input-label">
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </label>

          {error && <p className="error-text">{error}</p>}

          <button type="submit" className="primary-button" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="auth-divider divider">
          <span>or log in with</span>
        </div>

        <button
          type="button"
          className="google-button"
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          <FcGoogle size={20} />
          Continue with Google
        </button>

        <p className="auth-footer">
          New here? <Link to="/signup">Create an account</Link>
        </p>

        <div className="auth-trust">
          <PiShieldCheck size={16} color="var(--accent-base)" />
          Secure, encrypted login
        </div>
      </div>
    </div>
  );
}

export default Login;
